import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { Check, Clock, Globe2, GraduationCap } from 'lucide-react';
import PhoneInput from '../components/PhoneInput';
import Layout from '../components/Layout';
import { calculatePrice } from '../utils/pricing';
import { PLANS, DELIVERY_TIMES } from '../constants';
import { trackEvent } from '../lib/analytics';

export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [customerName, setCustomerName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [formErrors, setFormErrors] = useState({
    name: '',
    phone: '',
    submit: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [{ isPending }] = usePayPalScriptReducer();

  // Get order details from URL params or state
  const searchParams = new URLSearchParams(location.search);
  const orderDetails = {
    plan: searchParams.get('plan') || 'basic',
    isExperienced: searchParams.get('isExperienced') === 'true',
    language: searchParams.get('language') || 'englishOnly',
    deliveryTime: Number(searchParams.get('deliveryTime')) || 3
  };

  const currentPrice = calculatePrice(orderDetails);
  const plan = PLANS[orderDetails.plan as keyof typeof PLANS];
  const deliveryTime = DELIVERY_TIMES.find(t => t.days === orderDetails.deliveryTime);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const validateName = (name: string): string => {
    if (!name.trim()) return 'الاسم مطلوب';
    if (name.length < 3) return 'الاسم يجب أن يكون 3 أحرف على الأقل';
    if (!/^[\u0600-\u06FFa-zA-Z\s]+$/.test(name)) return 'الاسم يجب أن يحتوي على حروف فقط';
    return '';
  };

  const validatePhone = (phone: string): string => {
    if (!phone) return 'رقم الهاتف مطلوب';
    
    const countryCode = phone.slice(0, 4);
    const number = phone.slice(4);
    
    const validCountryCodes: { [key: string]: number } = {
      '+966': 9, // Saudi Arabia
      '+971': 9, // UAE
      '+974': 8, // Qatar
      '+973': 8, // Bahrain
      '+965': 8, // Kuwait
      '+968': 8, // Oman
    };

    if (!validCountryCodes[countryCode]) {
      return 'رمز الدولة غير صحيح';
    }

    if (number.length !== validCountryCodes[countryCode]) {
      return 'رقم الهاتف غير صحيح للدولة المحددة';
    }

    return '';
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomerName(value);
    setFormErrors(prev => ({ ...prev, name: validateName(value) }));
  };

  const handlePhoneChange = (value: string) => {
    setPhoneNumber(value);
    setFormErrors(prev => ({ ...prev, phone: validatePhone(value) }));
  };

  const handlePaymentSuccess = async (details: any) => {
    setIsSubmitting(true);
    try {
      const orderNumber = details.id;
      
      trackEvent('purchase_complete', {
        transaction_id: orderNumber,
        value: currentPrice,
        currency: 'SAR',
        items: [{
          item_name: plan.title,
          price: currentPrice
        }]
      });

      const whatsappMessage = encodeURIComponent(`مرحباً، أنا ${customerName}
تم إتمام الطلب بنجاح!
رقم الطلب: ${orderNumber}
الباقة المختارة: ${plan.title}
المستوى: ${orderDetails.isExperienced ? 'ذو خبرة' : 'حديث التخرج'}
وقت التسليم: ${deliveryTime?.label}
اللغة: ${getLanguageLabel(orderDetails.language)}
السعر الإجمالي: ${currentPrice} ريال`);
    
      window.location.href = `https://wa.me/966503083277?text=${whatsappMessage}`;
    } catch (error) {
      console.error('Error processing payment:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: 'حدث خطأ أثناء معالجة الدفع. الرجاء المحاولة مرة أخرى.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLanguageLabel = (lang: string) => {
    switch (lang) {
      case 'both':
        return 'عربي وإنجليزي';
      case 'arabicOnly':
        return 'عربي فقط';
      case 'englishOnly':
        return 'إنجليزي فقط';
      default:
        return '';
    }
  };

  const isFormValid = !formErrors.name && !formErrors.phone && customerName && phoneNumber;

  return (
    <Layout>
      <div className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-4xl font-bold mb-12 text-center">ملخص الطلب</h1>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-gray-900 text-white p-6">
              <h2 className="text-2xl font-bold">تفاصيل الطلب</h2>
            </div>

            <div className="p-8">
              <div className="space-y-8 mb-12">
                <div>
                  <h3 className="text-3xl font-bold mb-6">{plan.title}</h3>
                  <div className="grid gap-6 md:grid-cols-2 mb-8">
                    <div className="flex items-center gap-3 text-gray-600">
                      <GraduationCap className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span>المستوى: {orderDetails.isExperienced ? 'ذو خبرة' : 'حديث التخرج'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <Globe2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span>اللغة: {getLanguageLabel(orderDetails.language)}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600">
                      <Clock className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span>وقت التسليم: {deliveryTime?.label}</span>
                    </div>
                  </div>
                  <ul className="space-y-4 border-t border-gray-100 pt-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3 text-gray-600">
                        <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-green-50 rounded-xl p-6">
                  <div className="flex justify-between items-center text-2xl font-bold">
                    <span>السعر الإجمالي</span>
                    <span className="text-green-600">{currentPrice} ريال</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2 text-center">شامل ضريبة القيمة المضافة</p>
                </div>
              </div>

              <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الاسم الكامل <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={customerName}
                    onChange={handleNameChange}
                    className={`w-full px-4 py-3 border rounded-xl transition-colors ${
                      formErrors.name ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-green-500'
                    }`}
                    placeholder="أدخل اسمك الكامل"
                    disabled={isSubmitting}
                  />
                  {formErrors.name && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رقم الواتساب <span className="text-red-500">*</span>
                  </label>
                  <PhoneInput
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    error={formErrors.phone}
                    disabled={isSubmitting}
                  />
                  {formErrors.phone && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.phone}</p>
                  )}
                </div>

                <div className="paypal-button-container">
                  {isPending ? (
                    <div className="paypal-loading">
                      <div className="paypal-spinner"></div>
                    </div>
                  ) : !isFormValid ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <p className="text-yellow-800">
                        الرجاء إدخال الاسم ورقم الواتساب بشكل صحيح لإتمام عملية الدفع
                      </p>
                    </div>
                  ) : (
                    <PayPalButtons
                      style={{ 
                        layout: "vertical",
                        shape: "rect",
                        height: 55
                      }}
                      disabled={isSubmitting}
                      createOrder={(data, actions) => {
                        return actions.order.create({
                          purchase_units: [{
                            amount: {
                              value: (currentPrice / 3.75).toFixed(2),
                              currency_code: "USD"
                            },
                            description: `${plan.title} - ${customerName}`
                          }]
                        });
                      }}
                      onApprove={(data, actions) => {
                        return actions.order!.capture().then(handlePaymentSuccess);
                      }}
                      onError={(err) => {
                        console.error('PayPal Error:', err);
                        setFormErrors(prev => ({
                          ...prev,
                          submit: 'حدث خطأ أثناء عملية الدفع. الرجاء المحاولة مرة أخرى.'
                        }));
                      }}
                    />
                  )}
                </div>

                {formErrors.submit && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <p className="text-red-800">{formErrors.submit}</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}